import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Loader2, MapPin, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Phase E4 – Bestands-Assistent: ordnet Bestandsdokumentationen einmalig
 * Einsatzbereiche zu. Schritt-für-Schritt durch alle offenen Dokumentationen.
 * Schreibt ausschließlich über das bestehende PATCH /courses/{id}/einsatzbereiche
 * (setzt serverseitig einsatzbereich_reviewed=true). Kein Mitarbeiter-Eingriff.
 */
export const EinsatzbereichAssistent = ({
  open,
  onOpenChange,
  items,
  einsatzbereiche,
  onCompleted,
}) => {
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(new Set());
  const [vorschlagQuelle, setVorschlagQuelle] = useState(null);
  const [loadingVorschlag, setLoadingVorschlag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneCount, setDoneCount] = useState(0);

  const active = useMemo(
    () => einsatzbereiche.filter((e) => !e.archived),
    [einsatzbereiche]
  );

  const current = items[index] || null;
  const total = items.length;

  // Bei jedem Schritt den Hybrid-Vorschlag laden und vorauswählen.
  useEffect(() => {
    if (!open || !current) return;
    let cancelled = false;
    setLoadingVorschlag(true);
    setVorschlagQuelle(null);
    setSelected(new Set());
    api
      .getEinsatzbereichVorschlag(current.id)
      .then((res) => {
        if (cancelled) return;
        setSelected(new Set(res?.vorschlag_ids || []));
        setVorschlagQuelle(res?.quelle || null);
      })
      .catch(() => {
        if (!cancelled) setVorschlagQuelle(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingVorschlag(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, current]);

  // Beim Öffnen Zustand zurücksetzen.
  useEffect(() => {
    if (open) {
      setIndex(0);
      setDoneCount(0);
    }
  }, [open]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const advance = (didWrite) => {
    if (didWrite) setDoneCount((c) => c + 1);
    if (index + 1 >= total) {
      onOpenChange(false);
      const n = doneCount + (didWrite ? 1 : 0);
      toast.success(
        n > 0
          ? `${n} Dokumentation${n === 1 ? "" : "en"} zugeordnet.`
          : "Zuordnung abgeschlossen."
      );
      onCompleted?.();
    } else {
      setIndex((i) => i + 1);
    }
  };

  const save = async (ids) => {
    if (!current || saving) return;
    setSaving(true);
    try {
      await api.updateCourseEinsatzbereiche(current.id, ids);
      advance(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  const handleZuordnen = () => save(Array.from(selected));
  const handleLeer = () => save([]);
  const handleSkip = () => advance(false);

  if (!current) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="einsatzbereich-assistent-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <MapPin className="w-5 h-5 text-slate-700" />
            Einsatzbereiche zuordnen
          </DialogTitle>
          <DialogDescription>
            Ordnen Sie jede Dokumentation den passenden Einsatzbereichen zu.
            Danach werden neue Mitarbeiter automatisch versorgt.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div
            className="flex items-center justify-between"
            data-testid="assistent-progress"
          >
            <span className="text-xs uppercase tracking-widest text-slate-500 font-medium">
              Dokumentation {index + 1} von {total}
            </span>
            {vorschlagQuelle === "ki" && (
              <Badge
                className="bg-violet-50 text-violet-700 border-violet-200 text-[10px] uppercase tracking-wider gap-1"
                data-testid="assistent-vorschlag-quelle"
              >
                <Sparkles className="w-3 h-3" /> KI-Vorschlag
              </Badge>
            )}
            {vorschlagQuelle === "katalog" && (
              <Badge
                variant="outline"
                className="bg-slate-50 text-slate-600 border-slate-200 text-[10px] uppercase tracking-wider"
                data-testid="assistent-vorschlag-quelle"
              >
                Katalog-Vorschlag
              </Badge>
            )}
          </div>

          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p
              className="font-heading font-semibold text-slate-900"
              data-testid="assistent-doc-title"
            >
              {current.title}
            </p>
            {current.short_description && (
              <p className="text-sm text-slate-600 mt-1">
                {current.short_description}
              </p>
            )}
          </div>

          {loadingVorschlag ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Passende Einsatzbereiche werden vorgeschlagen…
            </div>
          ) : active.length === 0 ? (
            <p className="text-sm text-slate-500 italic">
              Noch keine Einsatzbereiche angelegt. Sie können diese Dokumentation
              als „betrifft keinen Einsatzbereich" markieren oder später zuordnen.
            </p>
          ) : (
            <div className="border border-slate-200 rounded-md divide-y divide-slate-100">
              {active.map((eb) => {
                const checked = selected.has(eb.id);
                return (
                  <label
                    key={eb.id}
                    htmlFor={`assistent-eb-${eb.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                  >
                    <Checkbox
                      id={`assistent-eb-${eb.id}`}
                      checked={checked}
                      onCheckedChange={() => toggle(eb.id)}
                      data-testid={`assistent-eb-checkbox-${eb.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900">
                        {eb.name}
                      </div>
                      {eb.description && (
                        <div className="text-xs text-slate-500 truncate">
                          {eb.description}
                        </div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          <p className="text-xs text-slate-500">
            Allgemeine Themen (z. B. Erste Hilfe, Brandschutz) brauchen keinen
            Einsatzbereich – dann auf „Betrifft keinen Einsatzbereich" tippen.
          </p>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            onClick={handleSkip}
            disabled={saving}
            data-testid="assistent-skip-button"
            className="text-slate-500"
          >
            Später
          </Button>
          <div className="flex flex-col-reverse sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={handleLeer}
              disabled={saving}
              data-testid="assistent-leer-button"
            >
              Betrifft keinen Einsatzbereich
            </Button>
            <Button
              onClick={handleZuordnen}
              disabled={saving || selected.size === 0}
              data-testid="assistent-zuordnen-button"
              className="bg-slate-900 hover:bg-slate-800 text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Zuordnen &amp; weiter
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EinsatzbereichAssistent;
